/**
 * Admin page for managing micro lesson prompts
 * Allows viewing, editing, and version history of prompts stored in database
 */

import { useState } from 'react';
import { AdminGuard } from '@/components/admin/AdminGuard';
import { PromptListTable } from '@/components/admin/prompts/PromptListTable';
import { PromptEditor } from '@/components/admin/prompts/PromptEditor';
import { PromptHistoryPanel } from '@/components/admin/prompts/PromptHistoryPanel';
import { useLessonPromptList } from '@/hooks/useLessonPrompts';
import { FileText, ArrowLeft, History, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export function AdminPrompts() {
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const { lessons, loading, error, refetch } = useLessonPromptList();

  const selectedLesson = lessons.find((l) => l.id === selectedLessonId);

  return (
    <AdminGuard>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Admin Dashboard
            </Link>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-purple-600" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Prompt Management</h1>
                  <p className="text-gray-600">
                    Edit micro lesson prompts, chat intros, and narratives
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Database className="w-4 h-4" />
                <span>
                  {lessons.filter((l) => l.hasDbPrompt).length} of {lessons.length} lessons have DB
                  overrides
                </span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          {selectedLessonId ? (
            // Editor View
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2">
                <div className="bg-white rounded-lg shadow-sm">
                  {/* Editor Header */}
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <button
                        onClick={() => setSelectedLessonId(null)}
                        className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-1"
                      >
                        <ArrowLeft className="w-3 h-3" />
                        Back to list
                      </button>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {selectedLesson?.title || selectedLessonId}
                      </h2>
                      {selectedLesson?.displayTitle && (
                        <p className="text-sm text-gray-500">{selectedLesson.displayTitle}</p>
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowHistory(!showHistory)}
                      className="flex items-center gap-2"
                    >
                      <History className="w-4 h-4" />
                      {showHistory ? 'Hide History' : 'View History'}
                    </Button>
                  </div>

                  {/* Editor Content */}
                  <PromptEditor
                    lessonId={selectedLessonId}
                    onSave={() => {
                      refetch();
                    }}
                  />
                </div>
              </div>

              {/* History Panel */}
              {showHistory && (
                <div className="xl:col-span-1">
                  <PromptHistoryPanel
                    lessonId={selectedLessonId}
                    onRestore={() => {
                      refetch();
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            // List View
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">All Lessons</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={refetch} disabled={loading}>
                    Refresh
                  </Button>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  Error loading lessons: {error.message}
                </div>
              )}

              <PromptListTable
                lessons={lessons}
                loading={loading}
                onSelectLesson={(id) => setSelectedLessonId(id)}
              />
            </div>
          )}
        </div>
      </div>
    </AdminGuard>
  );
}
